import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, AlertCircle } from 'lucide-react';
import {
  usePatientToothChart,
  useUpsertToothStatus,
  type ToothStatus,
} from '@/hooks/useDental';
import { useToothBadgeFlags } from '@/hooks/useToothBadges';
import { useShortcuts } from '@/hooks/useDentalShortcuts';
import type { ToothNotation, ToothData } from './types';
import { parseRootZones, serializeToothSurfaces } from './types';
import { ToothChartCanvas, type DentitionMode } from './ToothChartCanvas';
import { ToothChartZoom } from './ToothChartZoom';
import { ToothChartDentitionToggle } from './ToothChartDentitionToggle';
import { ToothNumberingToggle } from './ToothNumberingToggle';
import { ToothDetailDrawer } from './ToothDetailDrawer';
import { QuickRegistrationDrawer } from './shortcuts/QuickRegistrationDrawer';
import {
  DentalColorContext,
  mergeClinicColors,
} from './dentalColors';
import { ImageOverlayLayer } from './imaging/ImageOverlayLayer';
import { useDentalOverlay } from './imaging/DentalOverlayContext';
import { ChartLegendPopover } from './ChartLegendPopover';

// ── Constants ─────────────────────────────────────────────────────────────────

const NOTATION_STORAGE_KEY = 'clinio_dental_notation_preference';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ToothChartStageProps {
  patientId: string;
  clinicId: string;
  /** Optional JSONB palette from clinics.dental_chart_colors. Pass undefined to use system default. */
  clinicColors?: Record<string, unknown> | null;
  readOnly?: boolean;
  /** When set, clicking a tooth opens the QuickRegistrationDrawer instead of the detail drawer */
  activeShortcutId?: string | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ToothChartStage({ patientId, clinicId, clinicColors, readOnly = false, activeShortcutId }: ToothChartStageProps) {
  // Overlay — safe to call even without a provider (context has no-op defaults)
  const { pinnedImage } = useDentalOverlay();
  const { t } = useTranslation();

  // ── Persistent notation preference ────────────────────────────────────────
  const [notation, setNotation] = useState<ToothNotation>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem(NOTATION_STORAGE_KEY) as ToothNotation) || 'haderup';
    }
    return 'haderup';
  });

  useEffect(() => {
    localStorage.setItem(NOTATION_STORAGE_KEY, notation);
  }, [notation]);

  // ── Dentition toggle — default Voksen (no date_of_birth available) ────────
  const [dentition, setDentition] = useState<DentitionMode>('adult');

  // ── Zoom + pan ────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(100);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateRef = useRef(translate);
  translateRef.current = translate;

  const handleZoomChange = (n: number) => {
    setZoom(n);
    if (n <= 100) setTranslate({ x: 0, y: 0 });
  };

  const handleDentitionChange = (mode: DentitionMode) => {
    setDentition(mode);
    setZoom(100);
    setTranslate({ x: 0, y: 0 });
  };

  // Mouse drag for pan when zoom > 100%
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom <= 100) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX - translateRef.current.x, y: e.clientY - translateRef.current.y };
  }, [zoom]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setTranslate({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // ── Tooth selection ────────────────────────────────────────────────────────
  const [selectedTeeth, setSelectedTeeth] = useState<string[]>([]);
  const [quickRegTeeth, setQuickRegTeeth] = useState<string[]>([]);
  const [quickRegOpen, setQuickRegOpen] = useState(false);

  const { data: shortcuts } = useShortcuts(clinicId);
  const activeShortcut = shortcuts?.find((s) => s.id === activeShortcutId) ?? null;

  const handleToothClick = (fdi: string, e: React.MouseEvent) => {
    // If a shortcut is active, open the QuickRegistrationDrawer instead
    if (activeShortcutId && activeShortcut) {
      if (e.metaKey || e.ctrlKey) {
        setQuickRegTeeth((prev) =>
          prev.includes(fdi) ? prev.filter((f) => f !== fdi) : [...prev, fdi],
        );
      } else {
        setQuickRegTeeth([fdi]);
        setQuickRegOpen(true);
      }
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      setSelectedTeeth((prev) =>
        prev.includes(fdi) ? prev.filter((f) => f !== fdi) : [...prev, fdi],
      );
    } else {
      setSelectedTeeth([fdi]);
    }
  };

  // ── Data queries ────────────────────────────────────────────────────────────
  const {
    data: toothChart,
    isLoading,
    error: chartError,
  } = usePatientToothChart(patientId);

  const badgeMap = useToothBadgeFlags(patientId);

  // Realtime subscription is hoisted to DentalModulePage — one channel per
  // module mount instead of one per consuming component (see useDentalRealtime
  // implementation for the Supabase channel-reuse rationale).

  const upsertStatus = useUpsertToothStatus(clinicId);

  // ── Normalize data ─────────────────────────────────────────────────────────
  const toothDataArray: ToothData[] = useMemo(() => {
    if (!toothChart) return [];
    return (toothChart as ToothStatus[]).map((tooth): ToothData => {
      const rawSurfaces = (tooth.surfaces ?? {}) as Record<string, unknown>;
      const surfacesWithoutRoot = Object.fromEntries(
        Object.entries(rawSurfaces).filter(([k]) => k !== '_root_zones'),
      );
      const rootZones = parseRootZones(rawSurfaces);
      return {
        id: tooth.id,
        tooth_fdi: tooth.tooth_fdi,
        tooth_type: tooth.tooth_type,
        status: tooth.status,
        surfaces: surfacesWithoutRoot as ToothData['surfaces'],
        root_zones: rootZones.length > 0 ? rootZones : undefined,
        notes: tooth.notes,
        last_examined_at: tooth.last_examined_at,
      };
    });
  }, [toothChart]);

  const selectedToothData = useMemo((): ToothData | null => {
    if (selectedTeeth.length !== 1) return null;
    return (
      toothDataArray.find((t) => t.tooth_fdi === selectedTeeth[0]) ?? {
        id: `new-${selectedTeeth[0]}`,
        tooth_fdi: selectedTeeth[0],
        tooth_type: 'permanent',
        status: ['healthy'],
        surfaces: {},
        notes: '',
      }
    );
  }, [toothDataArray, selectedTeeth]);

  // ── Save handler ────────────────────────────────────────────────────────────
  const handleToothSave = async (tooth: ToothData) => {
    try {
      await upsertStatus.mutateAsync({
        patientId,
        toothFdi: tooth.tooth_fdi,
        toothType: tooth.tooth_type,
        status: tooth.status,
        surfaces: serializeToothSurfaces(tooth.surfaces, tooth.root_zones),
        notes: tooth.notes,
      });
      toast.success(`Tand ${tooth.tooth_fdi} gemt`);
      setSelectedTeeth([]);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Ukendt fejl ved gem';
      toast.error(`Kunne ikke gemme: ${message}`);
    }
  };

  // ── Color palette ──────────────────────────────────────────────────────────
  const colorPalette = useMemo(
    () => mergeClinicColors(clinicColors ?? null),
    [clinicColors],
  );

  // ── Loading / error states ─────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ds-muted" />
      </div>
    );
  }

  if (chartError) {
    return (
      <div className="rounded-lg border border-ds-red-line bg-ds-red-tint p-6 dark:border-red-900/50">
        <div className="flex items-center gap-2 text-ds-red-ink">
          <AlertCircle className="h-5 w-5" />
          <span>{t('common.error')}</span>
        </div>
      </div>
    );
  }

  const scaleFactor = zoom / 100;

  return (
    <DentalColorContext.Provider value={colorPalette}>
      <div className="w-full space-y-3">
        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ds-line bg-ds-card px-4 py-2.5 shadow-card">
          {/* Left: Dentition toggle */}
          <ToothChartDentitionToggle value={dentition} onChange={handleDentitionChange} />

          {/* Center: Notation toggle */}
          <div className="flex-1 flex justify-center">
            <ToothNumberingToggle value={notation} onChange={setNotation} />
          </div>

          {/* Right: Zoom */}
          <ToothChartZoom zoom={zoom} onChange={handleZoomChange} />
        </div>

        {/* ── Stage ─────────────────────────────────────────────────────────── */}
        <div
          role="grid"
          aria-label="Tandkort"
          className="relative w-full overflow-hidden rounded-xl"
          style={{ cursor: zoom > 100 ? 'grab' : 'default', minHeight: 320 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              transform: `scale(${scaleFactor}) translate(${translate.x / scaleFactor}px, ${translate.y / scaleFactor}px)`,
              transformOrigin: 'top center',
              transition: isDragging.current ? 'none' : 'transform 0.15s ease-out',
              willChange: 'transform',
            }}
          >
            <ToothChartCanvas
              teeth={toothDataArray}
              selectedTeeth={selectedTeeth}
              onToothClick={handleToothClick}
              notation={notation}
              dentition={dentition}
              readOnly={readOnly}
              badgeMap={badgeMap}
            />
          </div>

          {/* ── Image overlay ──────────────────────────────────────────────── */}
          {pinnedImage && (
            <ImageOverlayLayer image={pinnedImage} patientId={patientId} />
          )}

          {/* ── Floating legend popover ────────────────────────────────────── */}
          {!readOnly && <ChartLegendPopover />}
        </div>

        {/* ── Drawer ────────────────────────────────────────────────────────── */}
        <ToothDetailDrawer
          selectedTeeth={selectedTeeth}
          tooth={selectedToothData}
          notation={notation}
          onSave={handleToothSave}
          onClose={() => setSelectedTeeth([])}
          onClearSelection={() => setSelectedTeeth([])}
          readOnly={readOnly}
        />

        {/* ── Quick Registration Drawer (shortcut mode) ──────────────────────── */}
        <QuickRegistrationDrawer
          isOpen={quickRegOpen}
          onClose={() => {
            setQuickRegOpen(false);
            setQuickRegTeeth([]);
          }}
          patientId={patientId}
          clinicId={clinicId}
          shortcut={activeShortcut}
          toothFdis={quickRegTeeth}
        />
      </div>
    </DentalColorContext.Provider>
  );
}
